import { inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, defer, map, Observable, of, shareReplay } from 'rxjs';

import { ApiService } from '../../../shared/services/api.service';
import { withTransferState } from '../../../shared/utils/transfer-state.util';
import { PERRON_COINS_MODULE_KEYS, type PerronCoinsModuleKey, type PerronCoinsSettings } from './perron-coins.models';

// Clave de TransferState para pasar los ajustes del render server al cliente sin re-pedirlos tras la hidratación.
const PERRON_COINS_SETTINGS_KEY = makeStateKey<PerronCoinsSettings | null>('perron.coins.settings');

/** Respuesta cruda de `GET /perron-coins/settings` (M-JSON-1). Se lee como `unknown` campo a campo. */
interface PerronCoinsSettingsResponse {
  coin_value_crc?: unknown;
  modules?: unknown;
}

/**
 * Normaliza la respuesta: `coin_value_crc` vale solo si es un entero > 0 (si no, `null` = «valor desconocido», y la
 * página oculta el bloque del valor); cada flag es `true` solo si llega exactamente `true` (un `"true"` o un `1` →
 * `false`); las claves que la web no conoce se ignoran.
 */
function normalize(res: PerronCoinsSettingsResponse | null | undefined): PerronCoinsSettings | null {
  const value = res?.coin_value_crc;
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) return null;

  const raw = (res?.modules && typeof res.modules === 'object' ? res.modules : {}) as Record<string, unknown>;
  const modules = Object.fromEntries(PERRON_COINS_MODULE_KEYS.map((key) => [key, raw[key] === true])) as Record<
    PerronCoinsModuleKey,
    boolean
  >;
  return { coinValueCrc: value, modules };
}

/**
 * Cliente de `GET /perron-coins/settings` (A0·web, W1-W2). Público con `client.credentials` (D8): va por
 * `ApiService.get`, el mismo camino que `GET /web/home` (`HomeBlocksService`), así que el `authInterceptor` le pone
 * el token de usuario o, sin sesión, el de cliente, y renegocia este último ante un 401. Sin cabeceras propias.
 *
 * Calca `HomeBlocksService`: `TransferState` para que el cliente hidrate sin volver a pedirlo y `shareReplay(1)` como
 * caché de sesión. Un error HTTP da `null` sin toast (la página oculta el valor y sigue); el error no queda en la
 * caché (`shareReplay` se reinicia ante un error), así que la siguiente lectura vuelve a intentarlo.
 */
@Injectable({ providedIn: 'root' })
export class PerronCoinsSettingsService {
  private readonly api = inject(ApiService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly transferState = inject(TransferState);

  private cachedRequest$?: Observable<PerronCoinsSettings | null>;

  get(): Observable<PerronCoinsSettings | null> {
    if (!this.cachedRequest$) {
      // `defer` para que la request solo se dispare en un MISS del TransferState.
      this.cachedRequest$ = withTransferState(
        this.transferState,
        this.isBrowser,
        PERRON_COINS_SETTINGS_KEY,
        defer(() => this.api.get<PerronCoinsSettingsResponse>('/perron-coins/settings').pipe(map(normalize))),
      ).pipe(shareReplay(1));
    }
    return this.cachedRequest$.pipe(catchError(() => of(null)));
  }
}
